#!/usr/bin/env node

/**
 * agent-runtime CLI entry point
 *
 * Uses auto-generated program from cli-contract.yaml.
 */

import { existsSync, readFileSync, writeFileSync } from "node:fs";
import { resolve } from "node:path";
import { register } from "tsx/esm/api";
import { createProgram, type CommandHandlers } from "../generated/cli/program.js";
import { generate, loadConfig, checkFreshness } from "../generator/index.js";
import {
  buildTaskPrompt,
  pluginRegistry,
  runWorkflow,
  createGuardrailHooksFromRules,
  type AgentContractLike,
  type TaskContractLike,
  type SdkAdapter,
  type TaskContext,
  type WorkflowContractLike,
  type WorkflowInvocation,
  type GuardrailHooks,
  type GuardrailRuleData,
} from "../index.js";
import { loadPlugins } from "../lib/plugin-loader.js";
import { runDoctor } from "../lib/doctor.js";
import { runInit } from "../lib/init.js";
import { runImplement, formatImplementResult } from "../lib/implement.js";
import { runAuditImplementation, formatAuditResult } from "../lib/audit-implementation.js";
import { resolvedDsl } from "../builtin-agents/generated/index.js";
import { createProgressSink, type ProgressSink } from "../lib/progress-sink.js";
import type { ResolvedProgressLog } from "../generator/config.js";
import type { z } from "zod";
import YAML from "yaml";

register();

const VERSION = "0.36.5";

/**
 * Dynamically import the generated contracts from the configured generated_dir.
 * Handles both .ts (via tsx/ts-node) and .js (compiled) files,
 * and both flat exports and namespace exports.
 */
async function loadGeneratedContracts(configPath: string): Promise<{
  agentRegistry: Record<string, AgentContractLike>;
  taskRegistry: Record<string, TaskContractLike>;
  handoffSchemas: Record<string, z.ZodType>;
  workflowRegistry: Record<string, WorkflowContractLike>;
  guardrailHooks?: GuardrailHooks;
}> {
  const config = await loadConfig(configPath);
  const basePath = `${config.generatedDir}/index`;

  let indexPath: string;
  if (existsSync(`${basePath}.js`)) {
    indexPath = `${basePath}.js`;
  } else if (existsSync(`${basePath}.ts`)) {
    indexPath = `${basePath}.ts`;
  } else {
    throw new Error(
      `Generated index file not found at ${basePath}.{js,ts}. Run \`agent-runtime generate\` first.`,
    );
  }

  const mod = await import(new URL(`file://${indexPath}`).href);

  let guardrailHooks: GuardrailHooks | undefined;
  const resolvedDsl = mod.resolvedDsl as Record<string, unknown> | undefined;
  if (resolvedDsl) {
    const rawRules = resolvedDsl._guardrailRules as GuardrailRuleData | undefined;
    if (
      rawRules &&
      (rawRules.commandRules?.length ||
        rawRules.fileRules?.length ||
        rawRules.contentRules?.length)
    ) {
      guardrailHooks = createGuardrailHooksFromRules(rawRules);
    }
  }

  return {
    agentRegistry: mod.agentRegistry ?? mod.agents?.agentRegistry ?? {},
    taskRegistry: mod.taskRegistry ?? mod.tasks?.taskRegistry ?? {},
    handoffSchemas: mod.handoffSchemas ?? mod.handoffs?.handoffSchemas ?? {},
    workflowRegistry: mod.workflowRegistry ?? mod.workflows?.workflowRegistry ?? {},
    guardrailHooks,
  };
}

async function createAdapter(
  adapterName: string,
  guardrailHooks?: GuardrailHooks,
  cwd?: string,
  model?: string,
): Promise<SdkAdapter> {
  switch (adapterName) {
    case "mock": {
      const { MockAdapter } = await import("../adapters/mock.js");
      return new MockAdapter();
    }
    case "claude": {
      const { ClaudeAgentSdkAdapter } = await import("../adapters/claude-agent-sdk.js");
      return new ClaudeAgentSdkAdapter({ cwd, guardrailHooks, ...(model ? { model } : {}) });
    }
    case "gemini": {
      // The "gemini" selector now targets Gemini models via Google ADK, which
      // provides the native subagent mechanism required for LLM-internal routing.
      const { AdkSdkAdapter } = await import("../adapters/adk-sdk.js");
      return new AdkSdkAdapter({ guardrailHooks, ...(model ? { model } : {}) });
    }
    case "openai": {
      const { OpenAIAgentsSdkAdapter } = await import("../adapters/openai-agents-sdk.js");
      return new OpenAIAgentsSdkAdapter({ guardrailHooks, ...(model ? { model } : {}) });
    }
    default:
      throw new Error(`Unknown adapter: ${adapterName}. Available: mock, claude, gemini, openai`);
  }
}

function formatTable(headers: string[], rows: string[][]): string {
  const widths = headers.map((h, i) =>
    Math.max(h.length, ...rows.map((r) => (r[i] ?? "").length)),
  );
  const sep = widths.map((w) => "-".repeat(w)).join("  ");
  const pad = (s: string, w: number) => s.padEnd(w);
  const headerLine = headers.map((h, i) => pad(h, widths[i])).join("  ");
  const dataLines = rows.map((r) =>
    r.map((c, i) => pad(c ?? "", widths[i])).join("  "),
  );
  return [headerLine, sep, ...dataLines].join("\n");
}

function resolveProgressSink(
  cliLogFile: string | undefined,
  configLog: ResolvedProgressLog | undefined,
  quiet: boolean,
  label: string,
): ProgressSink | undefined {
  if (cliLogFile) {
    return createProgressSink({
      stderr: !quiet,
      file: resolve(cliLogFile),
      naming: "single",
      label,
    });
  }

  if (configLog && configLog.destination !== "none") {
    const dest = configLog.destination;
    return createProgressSink({
      stderr: !quiet && (dest === "stderr" || dest === "both"),
      file: (dest === "file" || dest === "both") ? configLog.file : undefined,
      naming: configLog.naming,
      label,
    });
  }

  if (quiet) return undefined;
  return createProgressSink({ stderr: true });
}

const handlers: CommandHandlers = {
  async init(options, _globalOpts) {
    try {
      const outputDir = resolve(options.output ?? ".");
      const result = await runInit({
        adapter: options.adapter ?? "mock",
        outputDir,
        force: options.force ?? false,
      });
      console.log(JSON.stringify(result, null, 2));
    } catch (err) {
      console.error((err as Error).message);
      process.exit(1);
    }
  },

  async generate(options, globalOpts) {
    try {
      const configPath = (globalOpts.config as string) ?? "./agents.conf.yaml";
      const result = await generate({
        clean: options.clean,
        check: options.check,
        templates: options.templates,
        configPath,
      });

      if (result.check_mode) {
        if (result.has_differences) {
          console.error("Generated files are out of date. Run `agent-runtime generate` to update.");
          process.exit(1);
        }
        console.log(JSON.stringify({ ...result, status: "up-to-date" }, null, 2));
      } else {
        console.log(JSON.stringify(result, null, 2));
      }
    } catch (err) {
      console.error("Generate failed:", (err as Error).message);
      process.exit(1);
    }
  },

  async run(workflowId, userRequest, options, globalOpts) {
    // --- Resolve input: --file takes precedence over positional args ---
    let resolvedWorkflowId = workflowId;
    let resolvedUserRequest = userRequest;
    let fileInvocation: WorkflowInvocation | undefined;

    if (options.file) {
      try {
        const raw = readFileSync(options.file, "utf8");
        const parsed = YAML.parse(raw) as Record<string, unknown>;
        resolvedWorkflowId = parsed.workflow as string;
        if (!resolvedWorkflowId) {
          console.error(`Error: invocation file "${options.file}" must contain a "workflow" field.`);
          process.exit(3);
        }
        fileInvocation = {
          workflow: resolvedWorkflowId,
          user_request: parsed.user_request as string | undefined,
          handoff: parsed.handoff as WorkflowInvocation["handoff"],
          runtime: parsed.runtime as WorkflowInvocation["runtime"],
          context: parsed.context as WorkflowInvocation["context"],
        };
        resolvedUserRequest = fileInvocation.user_request ?? "";
      } catch (err) {
        console.error(`Failed to read invocation file: ${(err as Error).message}`);
        process.exit(3);
      }
    }

    if (!resolvedWorkflowId) {
      console.error("Error: workflow-id is required (provide as argument or via --file)");
      process.exit(3);
    }
    if (!fileInvocation && !resolvedUserRequest) {
      console.error("Error: user-request is required (provide as argument or via --file)");
      process.exit(3);
    }

    const configPath = (globalOpts.config as string) ?? "./agents.conf.yaml";

    const fresh = await checkFreshness(configPath);
    if (!fresh) {
      console.error("Generated files are stale or missing. Run `agent-runtime generate` first.");
      process.exit(3);
    }

    const config = await loadConfig(configPath);

    const pluginResult = await loadPlugins({
      pluginPaths: config.pluginPaths.length > 0 ? config.pluginPaths : undefined,
      projectDir: config.configDir,
    });
    if (pluginResult.loaded > 0 && !(globalOpts.quiet as boolean)) {
      console.error(`Loaded ${pluginResult.loaded} plugin(s) from ${pluginResult.files.length} file(s)`);
    }
    for (const err of pluginResult.errors) {
      console.error(`Plugin load error (${err.file}): ${err.error}`);
    }

    let contracts: Awaited<ReturnType<typeof loadGeneratedContracts>>;
    try {
      contracts = await loadGeneratedContracts(configPath);
    } catch (err) {
      console.error(`Failed to load generated contracts: ${(err as Error).message}`);
      process.exit(1);
      return;
    }

    if (!contracts.workflowRegistry[resolvedWorkflowId]) {
      console.error(`Unknown workflow: ${resolvedWorkflowId}`);
      console.error(`Available workflows: ${Object.keys(contracts.workflowRegistry).join(", ")}`);
      process.exit(3);
      return;
    }

    const adapterName = (options.adapter as string) ?? "mock";
    const adapterCwd = fileInvocation?.context?.cwd ?? process.cwd();

    const { createModelResolver } = await import("../lib/model-resolver.js");
    type ModelClassType = import("../lib/model-resolver.js").ModelClass;
    const modelResolver = createModelResolver({
      modelMapping: config.model_mapping,
      fallbackAdapter: adapterName,
    });

    const adapterCache = new Map<string, SdkAdapter>();
    const modelAwareFactory = async (modelClass?: ModelClassType): Promise<SdkAdapter> => {
      const resolved = modelResolver.resolve(modelClass);
      const cacheKey = `${resolved.adapter}:${resolved.model ?? ""}`;
      const cached = adapterCache.get(cacheKey);
      if (cached) return cached;
      const adapter = await createAdapter(resolved.adapter, contracts.guardrailHooks, adapterCwd, resolved.model);
      adapterCache.set(cacheKey, adapter);
      return adapter;
    };

    let defaultAdapter: SdkAdapter;
    try {
      defaultAdapter = await modelAwareFactory();
    } catch (err) {
      console.error((err as Error).message);
      process.exit(12);
      return;
    }

    const hasModelMapping = Object.values(config.model_mapping).some(Boolean)
      || process.env.AGENT_RUNTIME_MODEL
      || process.env.AGENT_RUNTIME_MODEL_FAST
      || process.env.AGENT_RUNTIME_MODEL_STANDARD
      || process.env.AGENT_RUNTIME_MODEL_THINKING;

    const adapterOrFactory = hasModelMapping ? modelAwareFactory : defaultAdapter;

    const recovery = config.recovery;
    const maxRetries = options.maxRetries
      ? parseInt(options.maxRetries, 10)
      : undefined;
    const maxFollowUps = options.maxFollowUps
      ? parseInt(options.maxFollowUps, 10)
      : undefined;

    const registries = {
      workflowRegistry: contracts.workflowRegistry,
      taskRegistry: contracts.taskRegistry,
      agentRegistry: contracts.agentRegistry,
      handoffSchemas: contracts.handoffSchemas,
    };

    const onGate = options.autoApproveGates
      ? async () => true
      : async (gateKind: string, description: string) => {
          console.error(`Gate "${gateKind}": ${description}`);
          console.error("Use --auto-approve-gates to auto-approve. Rejecting by default.");
          return false;
        };

    const progressSink = resolveProgressSink(
      options.logFile as string | undefined,
      config.resolvedLogging.progressLog,
      globalOpts.quiet as boolean,
      resolvedWorkflowId,
    );

    let result;
    try {
      if (fileInvocation) {
        const invocation: WorkflowInvocation = {
          ...fileInvocation,
          runtime: {
            ...fileInvocation.runtime,
            maxFollowUps: maxFollowUps ?? fileInvocation.runtime?.maxFollowUps ?? recovery.max_follow_ups,
            maxRetries: maxRetries ?? fileInvocation.runtime?.maxRetries ?? recovery.max_retries,
          },
          hooks: { onGate },
          progressOutput: progressSink,
        };
        result = await runWorkflow(adapterOrFactory, invocation, registries);
      } else {
        result = await runWorkflow(adapterOrFactory, resolvedWorkflowId, {
          user_request: resolvedUserRequest!,
          maxRetries: maxRetries ?? recovery.max_retries,
          maxFollowUps: maxFollowUps ?? recovery.max_follow_ups,
          onGate,
          progressOutput: progressSink,
        }, registries);
      }
    } finally {
      progressSink?.close();
    }

    console.log(JSON.stringify(result, null, 2));

    if (result.status !== "completed") {
      process.exit(1);
    }
  },

  async list(resource, options, globalOpts) {
    if (!resource) {
      console.error("Error: resource is required (workflows, tasks, agents)");
      process.exit(1);
    }

    const validResources = ["workflows", "tasks", "agents"];
    if (!validResources.includes(resource)) {
      console.error(`Unknown resource: ${resource}`);
      console.error(`Available resources: ${validResources.join(", ")}`);
      process.exit(1);
      return;
    }

    const configPath = (globalOpts.config as string) ?? "./agents.conf.yaml";

    let contracts: Awaited<ReturnType<typeof loadGeneratedContracts>>;
    try {
      contracts = await loadGeneratedContracts(configPath);
    } catch (err) {
      console.error(`Failed to load generated contracts: ${(err as Error).message}`);
      console.error("Run `agent-runtime generate` first.");
      process.exit(1);
      return;
    }

    const format = options.format ?? "table";

    if (resource === "agents") {
      const agents = Object.values(contracts.agentRegistry);
      if (format === "json") {
        console.log(JSON.stringify(agents, null, 2));
      } else {
        if (agents.length === 0) {
          console.log("No agents defined.");
          return;
        }
        console.log(formatTable(
          ["ID", "Role", "Mode", "Purpose"],
          agents.map((a) => [a.id, a.role_name, a.mode, a.purpose]),
        ));
      }
    } else if (resource === "tasks") {
      const tasks = Object.values(contracts.taskRegistry);
      if (format === "json") {
        console.log(JSON.stringify(tasks, null, 2));
      } else {
        if (tasks.length === 0) {
          console.log("No tasks defined.");
          return;
        }
        console.log(formatTable(
          ["ID", "Agent", "Description"],
          tasks.map((t) => [t.id, t.target_agent, t.description]),
        ));
      }
    } else {
      const workflows = Object.values(contracts.workflowRegistry);
      if (format === "json") {
        console.log(JSON.stringify(workflows, null, 2));
      } else {
        if (workflows.length === 0) {
          console.log("No workflows defined.");
          return;
        }
        console.log(formatTable(
          ["ID", "Steps", "Description"],
          workflows.map((w) => [w.id, String(w.steps.length), w.description]),
        ));
      }
    }
  },

  async showPrompt(taskId, options, globalOpts) {
    if (!taskId) {
      console.error("Error: task-id is required");
      process.exit(1);
    }

    const configPath = (globalOpts.config as string) ?? "./agents.conf.yaml";

    let contracts: Awaited<ReturnType<typeof loadGeneratedContracts>>;
    try {
      contracts = await loadGeneratedContracts(configPath);
    } catch (err) {
      console.error(`Failed to load generated contracts: ${(err as Error).message}`);
      console.error("Run `agent-runtime generate` first.");
      process.exit(1);
      return;
    }

    const task = contracts.taskRegistry[taskId];
    if (!task) {
      console.error(`Unknown task: ${taskId}`);
      console.error(`Available tasks: ${Object.keys(contracts.taskRegistry).join(", ")}`);
      process.exit(1);
      return;
    }

    const agent = contracts.agentRegistry[task.target_agent];
    if (!agent) {
      console.error(`Unknown agent: ${task.target_agent} (referenced by task ${taskId})`);
      process.exit(1);
      return;
    }

    const showPromptConfig = await loadConfig(configPath);
    const pluginsApplied = options.withPlugins ?? false;
    if (pluginsApplied) {
      await loadPlugins({
        pluginPaths: showPromptConfig.pluginPaths.length > 0 ? showPromptConfig.pluginPaths : undefined,
        projectDir: showPromptConfig.configDir,
      });
    }

    const userRequest = options.userRequest ?? "(preview)";
    let context: TaskContext = { user_request: userRequest };

    if (pluginsApplied) {
      const modified = await pluginRegistry.runBeforeTask(taskId, context);
      if (modified === null) {
        console.error(`Task "${taskId}" skipped by plugin`);
        process.exit(1);
        return;
      }
      context = pluginRegistry.applyContextEnhancers(taskId, modified);
    }

    let prompt = buildTaskPrompt(agent, task, context, {
      handoffSchemas: contracts.handoffSchemas,
    });

    if (pluginsApplied) {
      prompt = pluginRegistry.applyPromptEnhancers(taskId, prompt, context);
    }

    const format = options.format ?? "markdown";
    if (format === "json") {
      console.log(JSON.stringify({
        task_id: taskId,
        agent_id: task.target_agent,
        prompt,
        handoff_schema: task.result_handoff,
        plugins_applied: pluginsApplied,
      }, null, 2));
    } else {
      console.log(prompt);
    }
  },

  async implement(description, options, globalOpts) {
    if (!description) {
      console.error("Error: description is required");
      process.exit(3);
    }

    const quiet = globalOpts.quiet as boolean;
    const progressSink = options.logFile
      ? createProgressSink({ stderr: !quiet, file: resolve(options.logFile), naming: "single" })
      : quiet ? undefined : createProgressSink({ stderr: true });

    let result;
    try {
      result = await runImplement(description, {
        adapter: options.adapter ?? "claude",
        projectDir: options.projectDir ?? ".",
        dryRun: options.dryRun ?? false,
        model: options.model as string | undefined,
        reportFormat: (options.reportFormat as "json" | "text" | "yaml") ?? "text",
        progressOutput: progressSink,
      });
    } finally {
      progressSink?.close();
    }

    if (result.status === "failed") {
      if (options.reportFormat === "json") {
        console.log(JSON.stringify({ error: result.error, status: "failed" }, null, 2));
      } else if (options.reportFormat === "yaml") {
        console.log(YAML.stringify({ error: result.error, status: "failed" }));
      } else {
        console.error(formatImplementResult(result));
      }
      process.exit(result.exitCode ?? 1);
    }

    const outputData = result.data ?? { status: result.status };
    let output: string;
    if (options.reportFormat === "json") {
      output = JSON.stringify(outputData, null, 2);
    } else if (options.reportFormat === "yaml") {
      output = YAML.stringify(outputData);
    } else {
      output = formatImplementResult(result);
    }

    const outputPath = options.output as string | undefined;
    if (outputPath) {
      writeFileSync(outputPath, output + "\n");
    } else {
      console.log(output);
    }
  },

  async auditImplementation(projectDir, options, globalOpts) {
    const quiet = globalOpts.quiet as boolean;
    const progressSink = options.logFile
      ? createProgressSink({ stderr: !quiet, file: resolve(options.logFile), naming: "single" })
      : quiet ? undefined : createProgressSink({ stderr: true });

    let result;
    try {
      result = await runAuditImplementation({
        adapter: options.adapter ?? "claude",
        projectDir: projectDir ?? ".",
        focus: (options.focus as "all" | "dsl" | "runtime" | "cli-contract" | "architecture") ?? "all",
        dryRun: options.dryRun ?? false,
        failOn: (options.failOn as "warning" | "error" | "critical") ?? "error",
        model: options.model as string | undefined,
        reportFormat: (options.reportFormat as "json" | "text" | "yaml") ?? "text",
        progressOutput: progressSink,
      });
    } finally {
      progressSink?.close();
    }

    if (result.status === "failed") {
      if (options.reportFormat === "json") {
        console.log(JSON.stringify({ error: result.error, status: "failed" }, null, 2));
      } else if (options.reportFormat === "yaml") {
        console.log(YAML.stringify({ error: result.error, status: "failed" }));
      } else {
        console.error(formatAuditResult(result));
      }
      process.exit(result.exitCode);
    }

    const outputData = result.data ?? { status: result.status };
    let output: string;
    if (options.reportFormat === "json") {
      output = JSON.stringify(outputData, null, 2);
    } else if (options.reportFormat === "yaml") {
      output = YAML.stringify(outputData);
    } else {
      output = formatAuditResult(result);
    }

    const outputPath = options.output as string | undefined;
    if (outputPath) {
      writeFileSync(outputPath, output + "\n");
    } else {
      console.log(output);
    }

    if (result.exitCode !== 0) {
      process.exit(result.exitCode);
    }
  },

  async agents(options, _globalOpts) {
    const format = options.format ?? "yaml";
    try {
      if (format === "json") {
        console.log(JSON.stringify(resolvedDsl, null, 2));
      } else {
        console.log(YAML.stringify(resolvedDsl, { lineWidth: 120 }));
      }
    } catch (err) {
      console.error(`Failed to output DSL: ${(err as Error).message}`);
      process.exit(1);
    }
  },

  async doctor(globalOpts) {
    const configPath = (globalOpts.config as string) ?? "./agents.conf.yaml";
    const result = await runDoctor(configPath);

    const statusIcon = (s: string) =>
      s === "pass" ? "✓" : s === "warn" ? "!" : "✗";

    for (const check of result.checks) {
      const icon = statusIcon(check.status);
      const msg = check.message ? `  ${check.message}` : "";
      console.log(`  ${icon} ${check.name.padEnd(20)} ${check.status.toUpperCase()}${msg}`);
    }

    console.log("");
    if (result.all_passed) {
      console.log("All checks passed.");
    } else {
      console.log("Some checks failed.");
    }

    console.log(JSON.stringify(result));

    if (!result.all_passed) {
      process.exitCode = 1;
    }
  },

};

const program = createProgram(handlers, VERSION);
await program.parseAsync();
